import {InjectionToken} from '@angular/core';
import {Observable} from 'rxjs';
import {EntryDraft, EntryPatch, MealPlanDay, MealPlanEntry, PoolItemDraft, RecipeSearchResult} from './meal-plan.models';
import {MealPlanSlot} from './meal-plan-slot.model';

/**
 * Transport seam for the meal planner, mirroring the `DIALOG_API` pattern.
 *
 * Implemented by `HttpMealPlanGateway` against the shipped REST API and wired in
 * `app.module.ts`. Components never see the implementation, only this interface's
 * view-model shapes; snake_case↔camelCase mapping lives entirely in the adapter.
 */
export interface MealPlanGateway {
  /** The household's ordered slot catalogue — the grid's row definition. */
  getSlots(householdId: number): Observable<MealPlanSlot[]>;

  /**
   * Days in `[from, to]` (inclusive, `'YYYY-MM-DD'`), empty days included.
   * One call backs the whole visible window.
   */
  getRange(householdId: number, from: string, to: string): Observable<MealPlanDay[]>;

  /** Persist an entry. Carries `draft.clientId`; echoes it back for reconciliation. */
  createEntry(householdId: number, draft: EntryDraft): Observable<MealPlanEntry>;

  /**
   * Patch `servings`, `note`, `plan_date`/`slot` (move), `sort_order` (reorder).
   * Entry-addressed — the endpoint doesn't take a household (access is by auth).
   */
  updateEntry(id: number, patch: EntryPatch): Observable<MealPlanEntry>;

  /** Entry-addressed delete. */
  deleteEntry(id: number): Observable<void>;

  /**
   * The household's pool: unplaced candidate meals (no date/slot). Independent of
   * the visible window — one call per household, refreshed on switch/reload.
   */
  getPool(householdId: number): Observable<MealPlanEntry[]>;

  /**
   * Add a candidate to the pool. Carries `draft.clientId`; echoes it back for
   * reconciliation, idempotent on retry like `createEntry`.
   */
  createPoolItem(householdId: number, draft: PoolItemDraft): Observable<MealPlanEntry>;

  /**
   * Un-place an entry back into the pool (clears its date/slot). Entry-addressed,
   * the inverse of a place, which is a `updateEntry({planDate, slot})` move.
   */
  unplaceEntry(id: number): Observable<MealPlanEntry>;

  /**
   * Search the household's recipes via the existing recipe API (`q` filter).
   * Household-scoped so the planner only offers recipes the household can use.
   */
  searchRecipes(householdId: number, query: string): Observable<RecipeSearchResult[]>;

  /**
   * Generate a set of meal ideas (from stock, food preferences, and the
   * household's saved recipes) into the pool as assistant-sourced entries.
   * Returns the newly created pool entries.
   */
  generatePool(householdId: number, count?: number, theme?: string): Observable<MealPlanEntry[]>;
}

export const MEAL_PLAN_GATEWAY = new InjectionToken<MealPlanGateway>('MEAL_PLAN_GATEWAY');
